import { openDB, type IDBPDatabase } from "idb";
import { Network } from "@capacitor/network";
import { App as CapApp } from "@capacitor/app";
import { ringLogger } from "./ringBufferLogger";
import type { DiagnosticPayload } from "./diagnostics";

export interface QueuedTicket {
  id: string;
  userEmail: string;
  userId?: string | null;
  category: string;
  message: string;
  hasDiagnostics: boolean;
  diagnostics?: DiagnosticPayload;
  createdAt: string;
  retryCount: number;
}

const SUPPORT_DB_NAME = "birch-support-db";
const SUPPORT_DB_VERSION = 1;
const SUPPORT_STORE = "queued_tickets";

interface SupportDB {
  queued_tickets: {
    key: string;
    value: QueuedTicket;
  };
}

let dbPromise: Promise<IDBPDatabase<SupportDB>> | null = null;
const memoryQueue = new Map<string, QueuedTicket>();

function hasIndexedDB(): boolean {
  return typeof window !== "undefined" && typeof indexedDB !== "undefined";
}

function getSupportDB(): Promise<IDBPDatabase<SupportDB>> | null {
  if (!hasIndexedDB()) return null;

  if (!dbPromise) {
    dbPromise = openDB<SupportDB>(SUPPORT_DB_NAME, SUPPORT_DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(SUPPORT_STORE)) {
          db.createObjectStore(SUPPORT_STORE, { keyPath: "id" });
        }
      },
    });
  }
  return dbPromise;
}

/**
 * Persists a support request to the offline queue
 */
export async function enqueueSupportTicket(ticket: Omit<QueuedTicket, "id" | "createdAt" | "retryCount">): Promise<QueuedTicket> {
  const queuedItem: QueuedTicket = {
    ...ticket,
    id: `ticket_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    createdAt: new Date().toISOString(),
    retryCount: 0,
  };

  const db = getSupportDB();
  if (db) {
    const database = await db;
    await database.put(SUPPORT_STORE, queuedItem);
  } else {
    memoryQueue.set(queuedItem.id, queuedItem);
  }

  ringLogger.breadcrumb("SupportQueue", "Ticket enqueued offline", { id: queuedItem.id, category: queuedItem.category });
  return queuedItem;
}

/**
 * Returns all tickets currently pending in the offline queue
 */
export async function getQueuedTickets(): Promise<QueuedTicket[]> {
  try {
    const db = getSupportDB();
    if (db) {
      const database = await db;
      return await database.getAll(SUPPORT_STORE);
    }
    return Array.from(memoryQueue.values());
  } catch (err) {
    console.warn("[SupportQueue] Failed to get queued tickets:", err);
    return Array.from(memoryQueue.values());
  }
}

/**
 * Removes a ticket once successfully transmitted
 */
export async function removeQueuedTicket(id: string): Promise<void> {
  try {
    const db = getSupportDB();
    if (db) {
      const database = await db;
      await database.delete(SUPPORT_STORE, id);
    } else {
      memoryQueue.delete(id);
    }
    ringLogger.breadcrumb("SupportQueue", "Ticket removed after successful sync", { id });
  } catch (err) {
    console.warn("[SupportQueue] Failed to delete ticket:", err);
  }
}

/**
 * Flushes the queue by submitting pending tickets
 */
let isFlushing = false;

export async function flushSupportQueue(
  sendFn: (ticket: QueuedTicket) => Promise<{ success: boolean; error?: string }>
): Promise<{ sent: number; failed: number }> {
  if (isFlushing) return { sent: 0, failed: 0 };
  isFlushing = true;

  let sent = 0;
  let failed = 0;

  try {
    const queued = await getQueuedTickets();
    if (queued.length === 0) {
      isFlushing = false;
      return { sent: 0, failed: 0 };
    }

    ringLogger.breadcrumb("SupportQueue", `Flushing ${queued.length} queued ticket(s)...`);

    for (const item of queued) {
      try {
        const res = await sendFn(item);
        if (res.success) {
          await removeQueuedTicket(item.id);
          sent++;
        } else {
          // Increment retry count
          item.retryCount += 1;
          const db = getSupportDB();
          if (db) {
            const database = await db;
            await database.put(SUPPORT_STORE, item);
          } else {
            memoryQueue.set(item.id, item);
          }
          failed++;
        }
      } catch (err) {
        console.warn(`[SupportQueue] Error sending ticket ${item.id}:`, err);
        failed++;
      }
    }
  } finally {
    isFlushing = false;
  }

  return { sent, failed };
}

/**
 * Initializes automatic queue flush when network reconnects or app resumes
 */
export function initSupportQueueAutoSync(
  sendFn: (ticket: QueuedTicket) => Promise<{ success: boolean; error?: string }>
): () => void {
  const handleOnline = () => {
    ringLogger.breadcrumb("SupportQueue", "Network online detected, triggering queue flush");
    flushSupportQueue(sendFn).catch(() => {});
  };

  // Browser online event
  if (typeof window !== "undefined") {
    window.addEventListener("online", handleOnline);
  }

  // Capacitor Network listener
  let networkListenerPromise: Promise<{ remove: () => void }> | null = null;
  try {
    networkListenerPromise = Network.addListener("networkStatusChange", (status) => {
      if (status.connected) {
        handleOnline();
      }
    });
  } catch {
    // Web fallback
  }

  // Capacitor App resume listener
  let appListenerPromise: Promise<{ remove: () => void }> | null = null;
  try {
    appListenerPromise = CapApp.addListener("appStateChange", ({ isActive }) => {
      if (isActive) {
        handleOnline();
      }
    });
  } catch {
    // Web fallback
  }

  // Initial check
  handleOnline();

  // Return cleanup
  return () => {
    if (typeof window !== "undefined") {
      window.removeEventListener("online", handleOnline);
    }
    networkListenerPromise?.then((h) => h.remove()).catch(() => {});
    appListenerPromise?.then((h) => h.remove()).catch(() => {});
  };
}
